import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBackIosNew';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import SearchIcon from '@mui/icons-material/Search';
import Box from '@mui/material/Box';
import InputBase from '@mui/material/InputBase';
import Skeleton from '@mui/material/Skeleton';
import Typography from '@mui/material/Typography';
import FilterDropdown from 'components/common/MasterFilter/FilterDropdown';
import { PrimaryButton } from 'components/forms/atoms/FormButtons';
import { useSelectedBusinessUnit } from 'contexts/SidebarContext';
import moment from 'moment';
import { useMemo, useState } from 'react';
import { useGetAppointmentsQuery } from 'services/api/appointments.api';
import { useGetBusinessUnitServiceCentersQuery } from 'services/api/businessUnits.api';
import {
  APPOINTMENT_TYPE_LABELS,
  APPOINTMENT_TYPES,
  type Appointment,
  type AppointmentType,
  appointmentClientName,
} from 'services/types/appointment';
import { NAVY, RADIUS_ITEM, SURFACE, SURFACE_CONTAINER_LOWEST, SURFACE_TINT, YELLOW } from 'styles/tokens';

import AppointmentDetail from './AppointmentDetail';
import BranchPicker from './BranchPicker';
import DaySummary from './DaySummary';
import MonthView from './MonthView';
import NewAppointmentModal from './NewAppointmentModal';
import { type ServiceCalendarView, TYPE_STYLES } from './serviceCalendar.constants';
import {
  buildWeekDays,
  capitalize,
  distinctAdvisors,
  formatLongDate,
  monthTitle,
  toMonthParam,
} from './serviceCalendar.utils';
import TimelineGrid from './TimelineGrid';

const VIEW_LABELS: Record<ServiceCalendarView, string> = { month: 'Mes', week: 'Semana', day: 'Día' };

const ServiceCalendar = () => {
  const { selectedBusinessUnitId } = useSelectedBusinessUnit();

  const [selectedBranch, setSelectedBranch] = useState<string | null>(null);
  const [anchor, setAnchor] = useState(() => moment());
  const [view, setView] = useState<ServiceCalendarView>('month');
  const [typeFilters, setTypeFilters] = useState<Set<AppointmentType>>(new Set());
  const [executiveFilters, setExecutiveFilters] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState('');
  const [showCancelled, setShowCancelled] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [detailAppointment, setDetailAppointment] = useState<Appointment | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);

  const { data: serviceCenters = [], isLoading: branchesLoading } = useGetBusinessUnitServiceCentersQuery(
    { businessUnitId: selectedBusinessUnitId as number },
    { skip: !selectedBusinessUnitId }
  );
  const branchNames = useMemo(() => serviceCenters.map((s) => s.name), [serviceCenters]);

  const { data: appointments = [], isLoading: appointmentsLoading } = useGetAppointmentsQuery(
    { businessUnitId: selectedBusinessUnitId as number, month: toMonthParam(anchor) },
    { skip: !selectedBusinessUnitId }
  );

  const monthCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    appointments.forEach((a) => {
      if (a.status === 'cancelled') return;
      counts[a.branchOffice] = (counts[a.branchOffice] ?? 0) + 1;
    });
    return counts;
  }, [appointments]);

  const branchAppointments = useMemo(
    () => appointments.filter((a) => a.branchOffice === selectedBranch),
    [appointments, selectedBranch]
  );

  const advisors = useMemo(() => distinctAdvisors(branchAppointments), [branchAppointments]);

  const visibleAppointments = useMemo(() => {
    const term = search.trim().toLowerCase();
    return branchAppointments.filter((a) => {
      if (!showCancelled && a.status === 'cancelled') return false;
      if (typeFilters.size > 0 && !typeFilters.has(a.type)) return false;
      if (executiveFilters.size > 0 && (a.advisorName == null || !executiveFilters.has(a.advisorName))) return false;
      if (term) {
        const haystack = `${appointmentClientName(a)} ${a.car?.licensePlate ?? ''}`.toLowerCase();
        if (!haystack.includes(term)) return false;
      }
      return true;
    });
  }, [branchAppointments, typeFilters, executiveFilters, search, showCancelled]);

  if (!selectedBusinessUnitId) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography sx={{ color: 'rgba(21, 24, 58, 0.6)' }}>
          Selecciona una unidad de negocio para ver la agenda.
        </Typography>
      </Box>
    );
  }

  if (!selectedBranch) {
    return (
      <Box sx={{ height: '100vh', overflow: 'auto', backgroundColor: SURFACE }}>
        <BranchPicker
          branches={serviceCenters}
          counts={monthCounts}
          isLoading={branchesLoading}
          appointmentsLoading={appointmentsLoading}
          onSelect={setSelectedBranch}
        />
      </Box>
    );
  }

  const goToToday = () => setAnchor(moment());
  const step = (direction: 1 | -1) => {
    const unit = view === 'month' ? 'month' : view === 'week' ? 'week' : 'day';
    setAnchor((prev) => prev.clone().add(direction, unit));
  };

  const onSelectAppointment = (appointment: Appointment) => {
    setDetailAppointment(appointment);
    setDetailOpen(true);
  };

  const periodTitle = view === 'day' ? `${capitalize(formatLongDate(anchor))}, ${anchor.year()}` : monthTitle(anchor);

  const Pill = ({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) => (
    <Box
      onClick={onClick}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.75,
        px: 1.5,
        py: 0.5,
        borderRadius: '999px',
        cursor: 'pointer',
        fontSize: 13,
        fontWeight: 600,
        whiteSpace: 'nowrap',
        color: active ? NAVY : 'rgba(21, 24, 58, 0.6)',
        backgroundColor: active ? YELLOW : SURFACE_CONTAINER_LOWEST,
        boxShadow: active ? 'none' : '0px 1px 3px rgba(21, 24, 58, 0.06)',
        transition: 'background-color 0.15s ease, color 0.15s ease',
        '&:hover': { backgroundColor: active ? YELLOW : 'rgba(21, 24, 58, 0.04)' },
      }}
    >
      {children}
    </Box>
  );

  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', backgroundColor: SURFACE }}>
      {/* Header */}
      <Box sx={{ px: 2, pt: 2.5, flexShrink: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1.5, flexWrap: 'wrap' }}>
            {/* Back to service centers */}
            <Box
              onClick={() => setSelectedBranch(null)}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
                ml: -0.5,
                mr: 2,
                mb: 0.75,
                px: 0.5,
                py: 0.25,
                borderRadius: RADIUS_ITEM,
                cursor: 'pointer',
                fontSize: 13,
                fontWeight: 600,
                color: 'rgba(21, 24, 58, 0.6)',
                '&:hover': { color: NAVY, backgroundColor: 'rgba(21, 24, 58, 0.06)' },
              }}
            >
              <ArrowBackIcon sx={{ fontSize: 16 }} /> Sucursales
            </Box>

            <FilterField label="Tipo">
              <FilterDropdown
                options={APPOINTMENT_TYPES.map((type) => ({
                  value: type,
                  label: APPOINTMENT_TYPE_LABELS[type],
                  color: TYPE_STYLES[type].rail,
                }))}
                selectedValues={[...typeFilters]}
                onChange={(values) => setTypeFilters(new Set(values as AppointmentType[]))}
              />
            </FilterField>

            <FilterField label="Ejecutivo">
              <FilterDropdown
                options={advisors.map((advisor) => ({ value: advisor, label: advisor }))}
                selectedValues={[...executiveFilters]}
                onChange={(values) => setExecutiveFilters(new Set(values))}
              />
            </FilterField>

            <FilterField label="Canceladas">
              <FilterDropdown
                options={[{ value: 'cancelled', label: 'Mostrar canceladas' }]}
                selectedValues={showCancelled ? ['cancelled'] : []}
                onChange={(values) => setShowCancelled(values.length > 0)}
              />
            </FilterField>
          </Box>
          <PrimaryButton onClick={() => setModalOpen(true)} startIcon={<AddIcon />} sx={{ flexShrink: 0 }}>
            Nueva cita
          </PrimaryButton>
        </Box>
        {/* Nav + view toggle */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            mt: 3,
            flexWrap: 'wrap',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <NavArrow onClick={() => step(-1)}>
                <ChevronLeftIcon fontSize="small" />
              </NavArrow>
              <NavArrow onClick={() => step(1)}>
                <ChevronRightIcon fontSize="small" />
              </NavArrow>
            </Box>
            <Pill active={false} onClick={goToToday}>
              Hoy
            </Pill>
            <Typography sx={{ fontSize: 18, fontWeight: 700, color: NAVY, ml: 1 }}>{periodTitle}</Typography>
            <Box
              sx={{
                ml: 2,
                px: 1.25,
                py: 0.25,
                borderRadius: RADIUS_ITEM,
                backgroundColor: SURFACE_TINT,
                fontSize: 12,
                fontWeight: 600,
                color: 'rgba(21, 24, 58, 0.75)',
              }}
            >
              {visibleAppointments.length} {visibleAppointments.length === 1 ? 'cita' : 'citas'}
            </Box>
            <Box
              sx={{
                ml: 1.5,
                px: 1.25,
                py: 0.25,
                borderRadius: RADIUS_ITEM,
                backgroundColor: SURFACE_TINT,
                fontSize: 12,
                fontWeight: 700,
                letterSpacing: '0.03em',
                color: NAVY,
              }}
            >
              {selectedBranch}
            </Box>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 1.5,
                py: 0.5,
                borderRadius: '999px',
                backgroundColor: SURFACE_TINT,
                width: 240,
              }}
            >
              <SearchIcon sx={{ fontSize: 18, color: 'rgba(21, 24, 58, 0.4)' }} />
              <InputBase
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar cliente o patente…"
                sx={{ fontSize: 13, flex: 1, color: NAVY }}
              />
            </Box>
            <Box sx={{ display: 'flex', gap: 0.5, p: 0.5, borderRadius: '999px', backgroundColor: SURFACE_TINT }}>
              {(['month', 'week', 'day'] as ServiceCalendarView[]).map((v) => (
                <Pill key={v} active={view === v} onClick={() => setView(v)}>
                  {VIEW_LABELS[v]}
                </Pill>
              ))}
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Calendar body */}
      <Box sx={{ flex: 1, minHeight: 0, display: 'flex', gap: 2, p: 2, pt: 1.5 }}>
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: SURFACE_CONTAINER_LOWEST,
            borderRadius: 3,
            boxShadow: '0px 1px 3px rgba(21, 24, 58, 0.08)',
            overflow: 'hidden',
            p: view === 'month' ? 1.5 : 0,
          }}
        >
          {appointmentsLoading ? (
            <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Skeleton variant="rectangular" height={40} />
              {Array.from({ length: 6 }).map((_, i) => (
                <Skeleton key={i} variant="rectangular" height={56} />
              ))}
            </Box>
          ) : view === 'month' ? (
            <MonthView
              anchor={anchor}
              appointments={visibleAppointments}
              onSelectAppointment={onSelectAppointment}
              onSelectDay={(day) => {
                setAnchor(day);
                setView('day');
              }}
            />
          ) : (
            <TimelineGrid
              days={view === 'week' ? buildWeekDays(anchor) : [anchor]}
              appointments={visibleAppointments}
              onSelect={onSelectAppointment}
            />
          )}
        </Box>

        {view === 'day' && (
          <DaySummary day={anchor} appointments={visibleAppointments} onSelectAppointment={onSelectAppointment} />
        )}
      </Box>

      <NewAppointmentModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        businessUnitId={selectedBusinessUnitId}
        branches={branchNames}
        defaultBranch={selectedBranch}
      />

      <AppointmentDetail appointment={detailAppointment} open={detailOpen} onClose={() => setDetailOpen(false)} />
    </Box>
  );
};

const NavArrow = ({ onClick, children }: { onClick: () => void; children: React.ReactNode }) => (
  <Box
    onClick={onClick}
    sx={{
      width: 32,
      height: 32,
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      color: 'rgba(21, 24, 58, 0.6)',
      backgroundColor: SURFACE_CONTAINER_LOWEST,
      boxShadow: '0px 1px 3px rgba(21, 24, 58, 0.06)',
      '&:hover': { color: NAVY, backgroundColor: 'rgba(21, 24, 58, 0.04)' },
    }}
  >
    {children}
  </Box>
);

const FilterField = ({
  label,
  width = 190,
  children,
}: {
  label: string;
  width?: number;
  children: React.ReactNode;
}) => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      width,
      '& .MuiButton-root': { width: '100%', justifyContent: 'space-between' },
    }}
  >
    <Typography
      sx={{
        fontFamily: 'Jost, sans-serif',
        fontWeight: 400,
        fontSize: '0.7rem',
        color: `${NAVY}80`,
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
        lineHeight: 1,
        mb: 0.75,
      }}
    >
      {label}
    </Typography>
    {children}
  </Box>
);

export default ServiceCalendar;
